import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { RoomSettings } from "@hitster/shared";
import { useGame } from "../hooks/useGameContext";
import { clearSession } from "../lib/storage";
import {
  clearCreateRoomSettingsDraft,
  getCreateRoomSettingsDraft,
  saveCreateRoomSettingsDraft,
} from "../lib/formDrafts";
import { RoomSettingsForm } from "../components/RoomSettingsForm";

const SERVER_TIMEOUT_MS = 15_000;

export function CreateRoomPage() {
  const { nickname, emitUserAction, connected, room, playerId, actionError, clearActionError } = useGame();
  const navigate = useNavigate();
  const pendingCreateRef = useRef(false);
  const [settings, setSettingsState] = useState<RoomSettings>(() => getCreateRoomSettingsDraft());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setSettings = (update: RoomSettings | ((prev: RoomSettings) => RoomSettings)) => {
    setSettingsState((prev) => {
      const next = typeof update === "function" ? update(prev) : update;
      saveCreateRoomSettingsDraft(next);
      return next;
    });
  };

  useEffect(() => {
    if (room && playerId && pendingCreateRef.current) {
      pendingCreateRef.current = false;
      setCreating(false);
      setError(null);
      clearCreateRoomSettingsDraft();
      navigate("/lobby");
    }
  }, [room, playerId, navigate]);

  useEffect(() => {
    if (!creating) return;
    const timeout = window.setTimeout(() => {
      if (!room) {
        setCreating(false);
        setError("Le serveur met du temps à répondre. Vos options sont conservées — réessayez dans un instant.");
      }
    }, SERVER_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [creating, room]);

  useEffect(() => {
    if (actionError && (creating || pendingCreateRef.current)) {
      setCreating(false);
      pendingCreateRef.current = false;
      setError(actionError);
      clearActionError();
    }
  }, [actionError, creating, clearActionError]);

  const handleCreate = () => {
    if (!connected) {
      setError("En attente de connexion au serveur…");
      return;
    }
    setError(null);
    clearActionError();
    clearSession();
    pendingCreateRef.current = true;
    setCreating(true);
    const sent = emitUserAction("room:create", { nickname, settings });
    if (!sent) {
      pendingCreateRef.current = false;
      setCreating(false);
      setError("Impossible de contacter le serveur. Vos options sont conservées.");
    }
  };

  return (
    <div className="max-w-xl mx-auto cw-panel p-8 space-y-6">
      <h1 className="cw-title text-3xl font-bold">Créer un salon</h1>
      <p className="text-violet-300/70">Hôte : <strong>{nickname}</strong></p>
      {!connected && (
        <p className="text-sm text-yellow-400" role="status">Connexion au serveur en cours…</p>
      )}
      {error && <p className="text-sm text-red-400" role="alert">{error}</p>}

      <RoomSettingsForm
        settings={settings}
        onChange={(patch) => setSettings((prev) => ({ ...prev, ...patch }))}
      />

      <button
        type="button"
        className="cw-btn cw-btn-primary w-full"
        disabled={creating || !connected || nickname.length < 2}
        onClick={handleCreate}
      >
        {creating ? "Création du salon…" : "Créer le salon"}
      </button>
    </div>
  );
}
